<template>
  <v-menu
    ref="datePickerMenu"
    v-model="menu"
    width="auto"
    transition="scale-transition"
    :close-on-content-click="false"
    :elevation="1"
  >
    <template #activator="{ props }">
      <AGTextField
        :id="id"
        append-icon="mdi-calendar-outline"
        autocomplete="off"
        clearable
        readonly
        :model-value="textFieldModel"
        :disabled="disabled"
        :hint="hint"
        :label="label"
        :persistent-hint="persistentHint"
        :persistent-placeholder="persistentPlaceholder"
        :placeholder="placeholder"
        :required="required"
        :rules="rules"
        :validate-on="validateOn"
        :message-color="messageColor"
        v-bind="props"
        @click:clear="onClear"
      />
    </template>

    <div v-click-outside="onCancel" class="ag-month-picker">
      <div class="ag-month-picker__header">
        <AGButtonIcon icon="md:chevron_left" size="md" @click="currentYear--" />

        <div class="ag-month-picker__value">
          {{ currentYear }}
        </div>

        <AGButtonIcon icon="md:chevron_right" size="md" @click="currentYear++" />
      </div>

      <div class="ag-month-picker__body">
        <button
          v-for="month in months"
          :key="month.value"
          :data-selected="isSelected(month.value)"
          @click="onSelectMonth(month.value)"
        >
          {{ month.label }}
        </button>
      </div>

      <div class="ag-date-picker-month__actions">
        <AGButton type="tertiary" @click="onCancel"> Cancelar </AGButton>
        <AGButton type="primary" @click="onConfirm"> Confirmar </AGButton>
      </div>
    </div>
  </v-menu>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGButtonIcon from '@/components/common/AGButtonIcon.vue';
import AGTextField from '@/components/common/AGTextField.vue';
import { MESSAGE_COLOR } from '@/constants/ConstantMessageColor';
import UtilsDate from '@/utils/UtilsDate';
import { uniqueId } from 'lodash';

export default {
  components: { AGButtonIcon, AGTextField, AGButton },

  emits: ['update:modelValue', 'change'],

  props: {
    id: {
      type: String,
      default: uniqueId('agDatePicker'),
    },

    label: {
      type: String,
      require: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
      default: 'Selecione uma data',
    },

    persistentPlaceholder: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: [Date, String, null],
      required: true,
    },

    persistentHint: {
      type: Boolean,
      default: true,
    },

    required: {
      type: Boolean,
      default: false,
    },

    rules: {
      type: Array,
      default: () => [],
    },

    validateOn: {
      type: String,
      default: 'input',
    },

    hint: {
      type: String,
      default: '',
    },

    valueFormat: {
      type: String,
      default: 'YYYY-MM',
    },

    displayFormat: {
      type: String,
      default: 'MM/YYYY',
    },

    messageColor: {
      type: String,
      default: MESSAGE_COLOR.DEFAULT,
      validator: (val) => Object.values(MESSAGE_COLOR).includes(val),
    },
  },

  data: () => ({
    menu: false,
    currentYear: new Date().getFullYear(),
    months: [
      { label: 'JAN', value: '01' },
      { label: 'FEV', value: '02' },
      { label: 'MAR', value: '03' },
      { label: 'ABR', value: '04' },
      { label: 'MAI', value: '05' },
      { label: 'JUN', value: '06' },
      { label: 'JUL', value: '07' },
      { label: 'AGO', value: '08' },
      { label: 'SET', value: '09' },
      { label: 'OUT', value: '10' },
      { label: 'NOV', value: '11' },
      { label: 'DEZ', value: '12' },
    ],
    selectedMonth: null,
  }),

  computed: {
    value: {
      get() {
        return this.modelValue;
      },

      set(value) {
        this.selectedMonth = value ? UtilsDate.toDate(value, this.valueFormat) : null;
        this.$emit('update:modelValue', value);
      },
    },

    textFieldModel() {
      if (!this.modelValue) {
        return '';
      }
      return UtilsDate.format2(this.modelValue, {
        fromFormat: this.valueFormat,
        toFormat: this.displayFormat,
      });
    },
  },

  watch: {
    modelValue() {
      this.update();
    },
  },

  mounted() {
    this.update();
  },

  methods: {
    isSelected(month) {
      return this.selectedMonth === `${this.currentYear}-${month}`;
    },
    onSelectMonth(month) {
      this.selectedMonth = `${this.currentYear}-${month}`;
    },
    closeMenu() {
      this.menu = false;
    },
    onCancel() {
      this.closeMenu();
      this.update();
    },
    onConfirm() {
      this.closeMenu();
      this.value = UtilsDate.format2(this.selectedMonth, {
        fromFormat: 'YYYY-MM',
        toFormat: this.valueFormat,
      });
    },
    onClear() {
      this.value = null;
      this.selectedMonth = null;
    },
    update() {
      this.selectedMonth = this.value
        ? UtilsDate.format2(this.value, { fromFormat: this.valueFormat, toFormat: 'YYYY-MM' })
        : null;
      if (this.selectedMonth) this.currentYear = Number(this.selectedMonth.slice(0, 4));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/datepicker.scss';

.ag-month-picker {
  width: 294px;
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow:
    0 5px 5px -3px rgba(0, 0, 0, 0.2),
    0 8px 10px 1px rgba(0, 0, 0, 0.14),
    0 3px 14px 2px rgba(0, 0, 0, 0.12);
  transition: background-color 0.25s ease;
  border-radius: 4px;
  font-weight: 400;
}

.ag-month-picker__header {
  min-height: 45px;
  padding: 4px 16px;
  align-items: center;
  display: flex;
  justify-content: space-between;
  position: relative;

  .ag-month-picker__value {
    text-align: center;
    font-size: $font-size-xxs;
    color: $neutral-color-high-medium !important;
    font-weight: 700;
  }
}

.ag-month-picker__body {
  padding: 0 8px;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 36px 36px 36px 36px;
  row-gap: 8px;
  column-gap: 4px;

  button {
    font-size: 12px;
    color: $neutral-color-high-light;
    font-weight: 400;
    border-radius: 4px;

    &:hover {
      background-color: $brand-color-highlight-light;
    }

    &[data-selected='true'] {
      color: $neutral-color-low-light;
      background-color: $brand-color-primary-medium;
      border: $border-width-hairline $border-default $brand-color-primary-medium;
    }
  }
}

.ag-date-picker-month__actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: right;
  gap: 8px;
  padding: 16px;
  margin-top: 16px;
}
</style>
